import React, { useState, useEffect, useContext } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { AuthContext, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, FileText, Download, Award, AlertCircle, CheckCircle, User, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const DocumentsPage = () => {
  const { token, user } = useContext(AuthContext);
  
  // Helper to check user roles
  const isHQAdmin = user?.role === 'HQ_ADMIN';
  
  const [documents, setDocuments] = useState([]);
  const [franchisees, setFranchisees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFranchisee, setSelectedFranchisee] = useState(null);
  const [viewLicenseDialog, setViewLicenseDialog] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    file_url: '',
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [docsRes, franchiseesRes] = await Promise.all([
        axios.get(`${API}/documents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/franchisees`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setDocuments(docsRes.data);
      setFranchisees(franchiseesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/documents`, {
        ...formData,
        uploaded_by: user.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document uploaded successfully');
      setOpenDialog(false);
      fetchData();
      setFormData({ title: '', category: '', file_url: '', description: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    }
  };

  const getLicenseStatus = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return { status: 'Expired', color: 'destructive', icon: AlertCircle };
    } else if (daysUntilExpiry <= 90) {
      return { status: 'Expiring Soon', color: 'secondary', icon: AlertCircle };
    } else {
      return { status: 'Active', color: 'default', icon: CheckCircle };
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'SOP': 'bg-primary',
      'Policy': 'bg-secondary',
      'Template': 'bg-chart-3',
      'License': 'bg-chart-1',
      'Other': 'bg-muted-foreground'
    };
    return colors[category] || 'bg-muted-foreground';
  };

  const groupedDocuments = documents.reduce((acc, doc) => {
    if (!acc[doc.category]) acc[doc.category] = [];
    acc[doc.category].push(doc);
    return acc;
  }, {});

  const handleViewLicense = (franchisee) => {
    setSelectedFranchisee(franchisee);
    setViewLicenseDialog(true);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="documents-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-heading font-bold">Documents & Licenses</h1>
            <p className="text-muted-foreground mt-1">Manage franchise licenses and document repository</p>
          </div>
          {isHQAdmin && (
            <Dialog open={openDialog} onOpenChange={setOpenDialog}>
              <DialogTrigger asChild>
                <Button data-testid="upload-document-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Upload Document
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Upload Document</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Title*</Label>
                    <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required data-testid="title-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Category*</Label>
                    <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})} required>
                      <SelectTrigger data-testid="category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SOP">SOP</SelectItem>
                        <SelectItem value="Policy">Policy</SelectItem>
                        <SelectItem value="Template">Template</SelectItem>
                        <SelectItem value="License">License Document</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>File URL*</Label>
                    <Input type="url" value={formData.file_url} onChange={(e) => setFormData({...formData, file_url: e.target.value})} required data-testid="file-url-input" />
                    <p className="text-xs text-muted-foreground">Enter a direct link to the file (e.g., Google Drive, Dropbox)</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea rows={3} value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} data-testid="description-input" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button type="submit" data-testid="submit-document-btn">Upload Document</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <Tabs defaultValue="licenses" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="licenses">Franchise Licenses</TabsTrigger>
            <TabsTrigger value="documents">Document Repository</TabsTrigger>
          </TabsList>

          {/* Licenses Tab */}
          <TabsContent value="licenses" className="space-y-6">
            <Card className="border-primary/20">
              <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10">
                <div className="flex items-center gap-3">
                  <Award className="h-8 w-8 text-primary" />
                  <div>
                    <CardTitle className="font-heading">Franchise License Management</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      View and manage all Little Caliphs franchise licenses
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                {loading ? (
                  <p className="text-center text-muted-foreground">Loading licenses...</p>
                ) : franchisees.length === 0 ? (
                  <div className="text-center py-12">
                    <Award className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No franchisee licenses found</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {franchisees.map((franchisee) => {
                      const licenseStatus = getLicenseStatus(franchisee.license.expiry_date);
                      const StatusIcon = licenseStatus.icon;
                      
                      return (
                        <Card 
                          key={franchisee.franchisee_id} 
                          className="border-2 hover:shadow-lg transition-all cursor-pointer relative overflow-hidden"
                          onClick={() => handleViewLicense(franchisee)}
                          data-testid={`license-card-${franchisee.franchise_id}`}
                        >
                          <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full" />
                          
                          <CardHeader className="relative">
                            <div className="flex items-start justify-between">
                              <Award className="h-10 w-10 text-primary" />
                              <Badge variant={licenseStatus.color} className="flex items-center gap-1">
                                <StatusIcon className="h-3 w-3" />
                                {licenseStatus.status}
                              </Badge>
                            </div>
                            <CardTitle className="text-lg mt-4">{franchisee.company_name}</CardTitle>
                          </CardHeader>
                          
                          <CardContent className="space-y-4">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-sm">
                                <span className="font-medium text-muted-foreground">License No:</span>
                                <span className="font-mono font-semibold text-primary">{franchisee.license.license_number}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="font-medium text-muted-foreground">Branch ID:</span>
                                <span className="font-semibold">{franchisee.franchise_id}</span>
                              </div>
                            </div>

                            <div className="pt-4 border-t space-y-3">
                              <div className="flex items-start gap-2">
                                <User className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs text-muted-foreground">Main Key Person</p>
                                  <p className="text-sm font-medium truncate">{franchisee.license.main_key_person_name}</p>
                                </div>
                              </div>
                              
                              <div className="flex items-start gap-2">
                                <Users className="h-4 w-4 text-secondary mt-1 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs text-muted-foreground">Secondary Key Person</p>
                                  <p className="text-sm font-medium truncate">{franchisee.license.secondary_key_person_name}</p>
                                </div>
                              </div>
                            </div>

                            <div className="pt-4 border-t grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <p className="text-muted-foreground">Issue Date</p>
                                <p className="font-medium">{new Date(franchisee.license.issue_date).toLocaleDateString()}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Expiry Date</p>
                                <p className="font-medium">{new Date(franchisee.license.expiry_date).toLocaleDateString()}</p>
                              </div>
                            </div>

                            <Button variant="outline" className="w-full mt-4" size="sm">
                              View Full Details
                            </Button>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-6">
            {loading ? (
              <p className="text-center text-muted-foreground">Loading documents...</p>
            ) : documents.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No documents uploaded yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedDocuments).map(([category, docs]) => (
                  <Card key={category}>
                    <CardHeader>
                      <CardTitle className="font-heading text-lg">{category}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {docs.map((doc) => (
                          <Card key={doc.document_id} className="border border-border/60 hover:shadow-md transition-shadow" data-testid={`document-${doc.title}`}>
                            <CardContent className="p-4">
                              <div className="flex items-start gap-3">
                                <div className={`${getCategoryColor(doc.category)} h-10 w-10 rounded-lg flex items-center justify-center text-white flex-shrink-0`}>
                                  <FileText className="h-5 w-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-medium text-sm truncate">{doc.title}</h3>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {new Date(doc.uploaded_at).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              {doc.description && (
                                <p className="text-xs text-muted-foreground mt-3 line-clamp-2">{doc.description}</p>
                              )}
                              <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="block mt-4">
                                <Button className="w-full" variant="outline" size="sm" data-testid={`download-${doc.title}`}>
                                  <Download className="h-4 w-4 mr-2" />
                                  View/Download
                                </Button>
                              </a>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* View License Details Dialog */}
        <Dialog open={viewLicenseDialog} onOpenChange={setViewLicenseDialog}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Award className="h-6 w-6 text-primary" />
                Franchise License Details
              </DialogTitle>
            </DialogHeader>
            {selectedFranchisee && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Company Name</p>
                      <p className="font-semibold text-lg">{selectedFranchisee.company_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">License Number</p>
                      <p className="font-mono font-bold text-primary text-lg">{selectedFranchisee.license.license_number}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Franchise ID</p>
                      <p className="font-semibold">{selectedFranchisee.franchise_id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Status</p>
                      <Badge variant={getLicenseStatus(selectedFranchisee.license.expiry_date).color}>
                        {getLicenseStatus(selectedFranchisee.license.expiry_date).status}
                      </Badge>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Kindergarten</p>
                      <p className="font-semibold">{selectedFranchisee.kindergarten.name}</p>
                      <p className="text-sm text-muted-foreground">{selectedFranchisee.kindergarten.location}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Issue Date</p>
                        <p className="font-semibold">{new Date(selectedFranchisee.license.issue_date).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Expiry Date</p>
                        <p className="font-semibold">{new Date(selectedFranchisee.license.expiry_date).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Users className="h-5 w-5 text-primary" />
                    Key Personnel
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="bg-primary/5 border-primary/20">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <User className="h-5 w-5 text-primary" />
                          <p className="font-semibold text-sm">Main Key Person</p>
                        </div>
                        <p className="font-medium text-lg">{selectedFranchisee.license.main_key_person_name}</p>
                        {selectedFranchisee.license.main_key_person_id && (
                          <p className="text-xs text-muted-foreground mt-1">
                            User ID: {selectedFranchisee.license.main_key_person_id.substring(0, 8)}...
                          </p>
                        )}
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-secondary/5 border-secondary/20">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Users className="h-5 w-5 text-secondary" />
                          <p className="font-semibold text-sm">Secondary Key Person</p>
                        </div>
                        <p className="font-medium text-lg">{selectedFranchisee.license.secondary_key_person_name}</p>
                        {selectedFranchisee.license.secondary_key_person_id && (
                          <p className="text-xs text-muted-foreground mt-1">
                            User ID: {selectedFranchisee.license.secondary_key_person_id.substring(0, 8)}...
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="font-semibold mb-4">Agreement Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Agreement Start</p>
                      <p className="font-medium">{new Date(selectedFranchisee.agreement_start_date).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Agreement Expiry</p>
                      <p className="font-medium">{new Date(selectedFranchisee.agreement_expiry_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default DocumentsPage;
